"use client";
import { brandAssets } from "constants/brand";
import React, { useState, useEffect, useRef } from "react";
import { useRouter, useParams, usePathname } from "next/navigation";
import Link from "next/link";
import MenuOverlay from "shared/MenuOverlay";
import { useAppDispatch, useAppSelector } from "appHooks";
import { fetchProjects } from "features/project/project.actions";
import { projectSelector } from "features/project/project.selectors";
import { clientSelector } from "features/client/client.selectors";
import { authSelector } from "features/auth/auth.selectors";
import { configSelector } from "features/config/config.selectors";
import Switcher from "shared/Switcher";
import { apiHeadersSelector } from "features/selectors";
import { UserHeaderComponentProps } from "./types";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import ThemeSwitcher from "components/ThemeSwitcher";
import { logoutUser } from "features/auth/auth.actions";
import { useTranslation } from "hooks/useTranslation";
import ProfileDropdown from "components/ProfileDropdown";

const UserHeaderComponent: React.FC<UserHeaderComponentProps> = ({
  showSkeleton,
}) => {
  const router = useRouter();
  const params = useParams();
  const dispatch = useAppDispatch();
  const [isFetched, setIsFetched] = useState(false);
  const isFetching = useRef(false); // Prevent multiple calls
  const { data: projectsData } = useAppSelector(projectSelector);
  const { data: clientData } = useAppSelector(clientSelector);
  const { data: authData } = useAppSelector(authSelector);
  const { data: userData } = useAppSelector(authSelector);
  const { locale } = useAppSelector(configSelector);
  // Use URL-derived locale for Link href to avoid SSR/client hydration mismatch
  const urlLocale = (params?.locale as string) ?? locale;
  const { slug } = useAppSelector(configSelector);
  const apiHeaders = useAppSelector(apiHeadersSelector);
  const [error, setError] = useState("");
  const [showSwitcher, setShowSwitcher] = useState(false);
  const [hasScrolled, setHasScrolled] = useState(false);
  const [menuActive, setMenuActive] = useState(false);
  const [headerMounted, setHeaderMounted] = useState(false);
  const pathname = usePathname();
  const [prevPathname, setPrevPathname] = useState(pathname);
  const t = useTranslation();

  // Synchronously remove position:fixed before commit phase on route changes
  // so InnerScrollAndFocusHandler.componentDidUpdate doesn't detect it
  if (pathname !== prevPathname) {
    setPrevPathname(pathname);
    setHeaderMounted(false);
    setMenuActive(false);
  }

  const getProjects = async () => {
    if (isFetched || isFetching.current) return;
    isFetching.current = true; // Set flag to true before fetching
    try {
      await dispatch(fetchProjects(apiHeaders));
    } catch (error: any) {
      setError(error?.message);
    } finally {
      isFetching.current = false;
      setIsFetched(true);
    }
  };

  useEffect(() => {
    setHeaderMounted(true);
    if (!showSkeleton) getProjects();

    const handleScroll = () => {
      if (window.scrollY > 0) {
        setHasScrolled(true);
      } else {
        setHasScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [showSkeleton]);

  useEffect(() => {
    setHeaderMounted(true);
  }, [pathname]);

  const AddActiveClass = () => {
    setMenuActive((prev) => !prev);
  };

  const GotoManageUsers = () => {
    router.push(`/${locale}/manage-users`);
  };

  const handleLogout = async () => {
    try {
      const result = await dispatch(
        logoutUser({
          is_sso_login: authData.is_sso_login,
          id_token: authData.id_token,
          locale,
          clientSlug: slug,
        }),
      );

      const payload = result.payload as { ssoLogout?: boolean } | undefined;
      if (!payload?.ssoLogout) {
        router.push(`/${locale}/login`);
      }
    } catch (error) {
      setError(t("somethingWentWrong"));
      console.log("error ", error);
      router.push(`/${locale}/login`);
    }
  };

  const HeaderSkeleton = () => (
    <header>
      <div className="header-wrap">
        <div className="logo">
          <Skeleton width={150} height={50} />
        </div>

        <div className="menu-block">
          <div style={{ display: "flex", gap: "16px" }}>
            <Skeleton circle width={24} height={24} />
            <Skeleton circle width={24} height={24} />
          </div>
        </div>
      </div>
    </header>
  );

  // Usage in the main component
  if (showSkeleton) return <HeaderSkeleton />;

  return (
    <>
      <header className={`${headerMounted ? "header-mounted" : ""} ${hasScrolled ? "scroll" : ""} ${menuActive ? "active" : ""}`}>
        <div className="header-wrap">
          <div className="logo">
            <Link href={`/${urlLocale}/`}>
              <img
                src={headerMounted ? (clientData?.light_logo || clientData?.logo || brandAssets.lightLogo) : brandAssets.lightLogo}
                alt="logo"
                className="logo-light-only"
                style={{
                  maxWidth: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
              <img
                src={headerMounted ? (clientData?.logo || clientData?.light_logo || brandAssets.darkLogo) : brandAssets.darkLogo}
                alt="logo"
                className="logo-dark-only"
                style={{
                  maxWidth: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            </Link>
          </div>

          <div className="menu-block">
            <i className="menu-icon" onClick={AddActiveClass} title={t("header.menu")}></i>
            <i className="cross-icon" onClick={AddActiveClass} title={t("header.closeMenu")}></i>
            <ProfileDropdown locale={locale} onLogout={handleLogout} />

            {headerMounted && userData.manage_user ? (
              <i className="group-icon" onClick={GotoManageUsers} title={t("header.manageUsers")}></i>
            ) : null}
            {headerMounted && userData?.clients?.length > 1 ? (
              <i
                className="switch-icon"
                onClick={() => setShowSwitcher(true)}
                title={t("header.switchOrganization")}
              ></i>
            ) : null}
            <ThemeSwitcher />
          </div>
        </div>
      </header>
      <MenuOverlay projects={projectsData} active={menuActive} />
      <Switcher
        open={showSwitcher}
        onClose={() => setShowSwitcher(false)}
        clients={userData.clients}
      />
    </>
  );
};

export default UserHeaderComponent;
